/**
 * Lo que comparten las pantallas de CAUCARED: colores, formato de plata, cuentas de vista
 * previa y el documento para imprimir o guardar en PDF.
 *
 * Los colores son otros A PROPÓSITO: Víctor pidió que CAUCARED quedara «bien diferenciado,
 * para no confundirse». LinkUs es azul-cian sobre azul oscuro; CAUCARED es el verde de su logo
 * sobre fondo verde oscuro. Si una pantalla se ve cian, es de LinkUs.
 */
import logoDeFabrica from './logo-caucared.png';

/**
 * El logo de CAUCARED viene con el sistema: si en Configuración no se ha subido otro, se usa
 * este. Dirección completa porque el PDF se arma en una ventana aparte, sin la del sitio.
 */
export const logoCau = (cfg) => (cfg?.logo_base64
  ? `data:${cfg.logo_mime || 'image/png'};base64,${cfg.logo_base64}`
  : new URL(logoDeFabrica, window.location.origin).href);

export const CAU = {
  acento: '#2FBF71',
  acentoSuave: 'rgba(47,191,113,0.12)',
  borde: 'rgba(47,191,113,0.3)',
  fondo: '#07120D',
  ambar: '#F59E0B',
  tarjeta: 'rgba(255,255,255,0.04)',
  lineas: 'rgba(255,255,255,0.09)',
  tenue: 'rgba(255,255,255,0.5)',
  verde: '#34D399', rojo: '#F87171', azul: '#60A5FA',
};

export const fmt = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(v) || 0);
export const fecha = (f) => {
  if (!f) return '—';
  const [a, m, d] = String(f).slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};
export const hoyISO = () => new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 10);
export const sumarDias = (f, dias) => {
  const d = new Date(`${f}T12:00:00`);
  d.setDate(d.getDate() + (parseInt(dias, 10) || 0));
  return d.toISOString().slice(0, 10);
};

/**
 * La misma cuenta que hace el servidor (services/caucared.js), solo para mostrarla mientras
 * se escribe. Lo que queda guardado lo calcula el servidor.
 *
 * El precio lleva el IVA INCLUIDO (decisión de Víctor): el IVA se saca de adentro.
 */
export const calcular = (items, ret = {}, responsableIva = true) => {
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const renglones = items.map((it) => {
    const cant = Math.max(n(it.cantidad), 0);
    const desc = Math.min(Math.max(n(it.descuento_pct), 0), 100);
    const ivaPct = responsableIva ? Math.max(n(it.iva_pct), 0) : 0;
    const total = Math.round(cant * n(it.precio_unitario) * (1 - desc / 100));
    const subtotal = ivaPct ? Math.round(total / (1 + ivaPct / 100)) : total;
    return { subtotal, iva: total - subtotal, total };
  });
  const subtotal = renglones.reduce((s, r) => s + r.subtotal, 0);
  const iva = renglones.reduce((s, r) => s + r.iva, 0);
  const total = subtotal + iva;
  const retefuente = ret.aplica_retefuente ? Math.round(subtotal * n(ret.retefuente_pct) / 100) : 0;
  const reteica = ret.aplica_reteica ? Math.round(subtotal * n(ret.reteica_pct) / 100) : 0;
  const reteiva = ret.aplica_reteiva ? Math.round(iva * 15 / 100) : 0;
  return { renglones, subtotal, iva, total, retefuente, reteica, reteiva, neto: total - retefuente - reteica - reteiva };
};

/** El costo sin su IVA: el que se pagó al comprar se descuenta (ver services/caucared.js). */
export const costoSinIva = (costo, ivaPct) => Number(costo) / (1 + (Number(ivaPct) || 0) / 100);

export const ESTADOS_COT = {
  borrador:  { txt: 'Borrador',  color: '#9CA3AF' },
  enviada:   { txt: 'Enviada',   color: CAU.azul },
  aceptada:  { txt: 'Aceptada',  color: CAU.verde },
  rechazada: { txt: 'Rechazada', color: CAU.rojo },
  facturada: { txt: 'Facturada', color: CAU.acento },
};
export const ESTADOS_PAGO = {
  pendiente: { txt: 'Pendiente', color: CAU.azul },
  parcial:   { txt: 'Abonada',   color: CAU.ambar },
  vencida:   { txt: 'Vencida',   color: CAU.rojo },
  pagada:    { txt: 'Pagada',    color: CAU.verde },
  anulada:   { txt: 'Anulada',   color: '#6B7280' },
};

export const Pastilla = ({ info }) => (
  <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
    color: info?.color, background: (info?.color || '#999') + '22', border: `1px solid ${(info?.color || '#999')}55`, whiteSpace: 'nowrap' }}>
    {info?.txt || '—'}
  </span>
);

export const est = {
  tarjeta: { background: CAU.tarjeta, border: `1px solid ${CAU.lineas}`, borderRadius: '12px', padding: '16px' },
  input: { background: 'rgba(255,255,255,0.06)', border: `1px solid ${CAU.lineas}`, borderRadius: '8px', padding: '9px 11px',
    color: '#fff', fontSize: '14px', width: '100%', boxSizing: 'border-box', minWidth: 0 },
  label: { fontSize: '11px', color: CAU.tenue, marginBottom: '4px', display: 'block', fontWeight: 600 },
  boton: { background: CAU.acento, color: '#03170C', border: 'none', borderRadius: '8px', padding: '10px 16px',
    fontWeight: 700, fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap' },
  botonSec: { background: 'transparent', color: '#fff', border: `1px solid ${CAU.lineas}`, borderRadius: '8px',
    padding: '9px 14px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
  error: { background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.35)', color: '#FCA5A5',
    borderRadius: '8px', padding: '10px 12px', fontSize: '13px' },
  th: { textAlign: 'left', padding: '10px', fontSize: '11px', color: CAU.tenue, fontWeight: 700, borderBottom: `1px solid ${CAU.lineas}`, whiteSpace: 'nowrap' },
  td: { padding: '10px', fontSize: '13px', borderBottom: `1px solid ${CAU.lineas}` },
};

export const mensajeDeError = (e, porDefecto = 'No se pudo completar') =>
  e?.response?.data?.error || (e?.response ? `${porDefecto} (error ${e.response.status})` : `${porDefecto}: no hay conexión con el servidor`);

// ─── El documento impreso ────────────────────────────────────

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const conSaltos = (v) => esc(v).replace(/\n/g, '<br>');

/**
 * Abre el documento en una ventana y lanza la impresión (de ahí se guarda como PDF).
 * NUNCA lleva costo, ganancia ni margen: es lo que ve el cliente.
 */
export const imprimir = (doc, cfg, tipo) => {
  const esFactura = tipo === 'factura';
  const color = cfg.color || CAU.acento;
  const titulo = esFactura ? (cfg.titulo_factura || 'FACTURA DE VENTA') : 'COTIZACIÓN';
  const vence = esFactura ? doc.fecha_vencimiento : sumarDias(String(doc.fecha).slice(0, 10), doc.validez_dias || 15);
  const logo = '<img src="' + logoCau(cfg) + '" style="max-height:110px;max-width:260px;object-fit:contain" alt="">';
  const hayIva = (doc.items || []).some((it) => Number(it.iva_pct) > 0);
  const hayDesc = (doc.items || []).some((it) => Number(it.descuento_pct) > 0);

  const filas = (doc.items || []).map((it, i) =>
    '<tr>' +
      '<td class="c">' + (i + 1) + '</td>' +
      '<td>' + conSaltos(it.descripcion) + (it.sku ? '<div class="sku">Ref. ' + esc(it.sku) + '</div>' : '') + '</td>' +
      '<td class="c">' + Number(it.cantidad) + '</td>' +
      '<td class="r">' + fmt(it.precio_unitario) + '</td>' +
      (hayDesc ? '<td class="c">' + (Number(it.descuento_pct) ? Number(it.descuento_pct) + '%' : '—') + '</td>' : '') +
      (hayIva ? '<td class="c">' + Number(it.iva_pct) + '%</td>' : '') +
      '<td class="r b">' + fmt(Number(it.subtotal) + Number(it.iva)) + '</td>' +
    '</tr>').join('');

  const fila = (a, b, clase = '') => '<div class="tr ' + clase + '"><span>' + a + '</span><span>' + b + '</span></div>';
  let totales = fila(hayIva || Number(doc.iva) ? 'Subtotal sin IVA' : 'Subtotal', fmt(doc.subtotal));
  if (hayIva || Number(doc.iva)) totales += fila('IVA', fmt(doc.iva));
  totales += fila('TOTAL', fmt(doc.total), 'fin');
  if (esFactura) {
    const ret = [];
    if (Number(doc.retefuente)) ret.push(fila('Retención en la fuente (' + Number(doc.retefuente_pct) + '%)', '−' + fmt(doc.retefuente)));
    if (Number(doc.reteica)) ret.push(fila('Retención de ICA (' + Number(doc.reteica_pct) + '%)', '−' + fmt(doc.reteica)));
    if (Number(doc.reteiva)) ret.push(fila('Retención de IVA (15%)', '−' + fmt(doc.reteiva)));
    if (ret.length) totales += ret.join('') + fila('NETO A PAGAR', fmt(doc.neto), 'neto');
  }

  const empresa = [
    cfg.nit ? '<b>NIT ' + esc(cfg.nit) + '</b>' : '',
    // Quien no cobra IVA lo dice en el documento, para que el cliente no lo eche de menos
    cfg.responsable_iva === false ? 'No responsable de IVA' : '',
    esc([cfg.direccion, cfg.ciudad].filter(Boolean).join(', ')),
    esc([cfg.telefono, cfg.email].filter(Boolean).join(' · ')),
    esc(cfg.website || ''),
  ].filter(Boolean).join('<br>');

  const condiciones = esFactura ? '' : (doc.condiciones || cfg.condiciones || '');
  const html = '<!doctype html><html><head><meta charset="utf-8"><title>' + esc(titulo + ' ' + doc.numero) + '</title><style>' +
    '*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,Helvetica,sans-serif;color:#1f2937;font-size:12.5px;padding:28px 36px}' +
    '.top{display:flex;justify-content:space-between;gap:20px;padding-bottom:16px;border-bottom:4px solid ' + color + ';margin-bottom:18px}' +
    '.emp{font-size:11.5px;color:#4b5563;line-height:1.6;margin-top:8px}.doc{text-align:right}' +
    '.tit{font-size:22px;font-weight:900;color:' + color + ';letter-spacing:.5px}.num{font-size:15px;font-weight:700;margin-top:4px}' +
    '.meta{font-size:11.5px;color:#4b5563;margin-top:3px}.anulada{color:#dc2626;font-weight:900;font-size:16px;margin-top:6px}' +
    '.cli{border:1px solid #e5e7eb;border-left:4px solid ' + color + ';border-radius:6px;padding:10px 14px;margin-bottom:16px;line-height:1.6}' +
    '.lbl{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:1px}.cn{font-size:14px;font-weight:700}' +
    'table{width:100%;border-collapse:collapse;margin-bottom:14px}th{background:' + color + ';color:#fff;padding:8px;font-size:10.5px;text-transform:uppercase;text-align:left}' +
    'td{padding:8px;border-bottom:1px solid #e5e7eb;vertical-align:top}.c{text-align:center}.r{text-align:right;white-space:nowrap}.b{font-weight:700}' +
    '.sku{font-size:10px;color:#9ca3af;margin-top:2px}.tots{margin-left:auto;width:320px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;margin-bottom:16px}' +
    '.tr{display:flex;justify-content:space-between;padding:7px 12px;border-bottom:1px solid #f3f4f6}' +
    '.fin{background:' + color + ';color:#fff;font-weight:900;font-size:14px}.neto{background:#111827;color:#fff;font-weight:900;font-size:14px}' +
    '.caja{border:1px solid #e5e7eb;border-radius:6px;padding:10px 14px;margin-bottom:12px;line-height:1.6;font-size:11.5px;color:#374151}' +
    '.pie{text-align:center;color:#6b7280;font-size:10.5px;border-top:1px solid #e5e7eb;padding-top:10px;margin-top:16px;line-height:1.6}' +
    '@media print{body{padding:10px 14px}}</style></head><body>' +
    '<div class="top"><div>' + logo + '<div class="emp"><b>' + esc(cfg.nombre || '') + '</b><br>' + empresa + '</div></div>' +
      '<div class="doc"><div class="tit">' + esc(titulo) + '</div><div class="num">N.º ' + esc(doc.numero) + '</div>' +
      '<div class="meta">Fecha: ' + fecha(doc.fecha) + '</div>' +
      '<div class="meta">' + (esFactura ? 'Vence: ' : 'Válida hasta: ') + fecha(vence) + '</div>' +
      (esFactura ? '<div class="meta">Forma de pago: ' + (doc.forma_pago === 'contado' ? 'Contado' : 'Crédito') + '</div>' : '') +
      (esFactura && doc.numero_fe ? '<div class="meta">Factura electrónica: ' + esc(doc.numero_fe) + '</div>' : '') +
      (doc.estado === 'anulada' ? '<div class="anulada">ANULADA</div>' : '') +
    '</div></div>' +
    '<div class="cli"><div class="lbl">' + (esFactura ? 'Facturado a' : 'Cotizado a') + '</div>' +
      '<div class="cn">' + esc(doc.cliente_nombre) + '</div>' +
      (doc.cliente_documento ? '<div>' + esc(doc.cliente_documento) + '</div>' : '') +
      (doc.cliente_contacto ? '<div>Atención: ' + esc(doc.cliente_contacto) + '</div>' : '') +
      '<div>' + esc([doc.cliente_direccion, doc.cliente_telefono, doc.cliente_email].filter(Boolean).join(' · ')) + '</div>' +
    '</div>' +
    '<table><thead><tr><th class="c" style="width:32px">#</th><th>Descripción</th><th class="c">Cant.</th><th class="r">Valor unit.' + (hayIva ? ' (IVA incl.)' : '') + '</th>' +
      (hayDesc ? '<th class="c">Desc.</th>' : '') + (hayIva ? '<th class="c">IVA</th>' : '') + '<th class="r">Total</th></tr></thead>' +
    '<tbody>' + filas + '</tbody></table>' +
    '<div class="tots">' + totales + '</div>' +
    (condiciones ? '<div class="caja"><div class="lbl">Condiciones</div>' + conSaltos(condiciones) + '</div>' : '') +
    (doc.notas ? '<div class="caja"><div class="lbl">Observaciones</div>' + conSaltos(doc.notas) + '</div>' : '') +
    (cfg.leyenda_legal ? '<div class="caja">' + conSaltos(cfg.leyenda_legal) + '</div>' : '') +
    '<div class="pie">' + conSaltos(cfg.pie || 'Gracias por su confianza') + '</div>' +
    '</body></html>';

  const w = window.open('', '_blank');
  if (!w) { alert('El navegador bloqueó la ventana. Permite las ventanas emergentes para este sitio.'); return; }
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 400);
};

/** El texto para mandar por WhatsApp. El PDF se adjunta aparte. Nunca lleva costos. */
export const abrirWhatsapp = (doc, cfg, tipo) => {
  const tel = String(doc.cliente_telefono || '').replace(/\D/g, '');
  const nombre = (doc.cliente_contacto || doc.cliente_nombre || '').split(' ')[0];
  const esFactura = tipo === 'factura';
  const lineas = [
    `Hola${nombre ? ' ' + nombre : ''}, le saluda ${cfg.nombre || 'CAUCARED'}.`,
    esFactura
      ? `Le compartimos la factura ${doc.numero} por ${fmt(doc.neto || doc.total)}, con vencimiento el ${fecha(doc.fecha_vencimiento)}.`
      : `Le compartimos la cotización ${doc.numero} por ${fmt(doc.total)}${Number(doc.iva) ? ' (IVA incluido)' : ''}, válida hasta el ${fecha(sumarDias(String(doc.fecha).slice(0, 10), doc.validez_dias || 15))}.`,
    'Adjunto el documento en PDF. Quedamos atentos a cualquier inquietud.',
  ];
  const destino = tel ? (tel.length === 10 ? '57' + tel : tel) : '';
  window.open(`https://wa.me/${destino}?text=${encodeURIComponent(lineas.join('\n\n'))}`, '_blank');
};
